import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { formatInTimeZone } from "date-fns-tz";

import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  RiAddCircleLine,
  RiCloseLine,
  RiExportLine,
  RiSendPlaneLine,
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiCheckLine,
  RiCloseFill,
  RiExternalLinkLine,
  RiRefreshLine,
  RiResetRightLine,
  RiCheckDoubleLine,
  RiFileTextLine,
} from "@remixicon/react";

import { useError } from "../../contexts/ErrorContext";
import { useDialog } from "../../hooks/useDialog";
import { useShippingDataCoordinator } from "../../hooks/useShippingDataCoordinator";

import {
  ShippingDialogTypeT,
  ShippingDialogTypesT,
  SelectedShippingData_T,
  SelectedShippingGroup_T,
} from "../../types/Shipping";
import type { ShippingDetailRecord_T } from "../../types/Shipping";

import GaneshDataGrid from "../DataGrid/GaneshDataGrid";
import ShippingStatusSummary from "./ShippingStatusSummary";
import {
  RefreshExportDialog,
  ExportDialog,
  CreateShipmentDialog,
  DeliverShipmentDialog,
  MarkDeliveredDialog,
  ChecksumDialog,
} from "./Dialogs";
import ExtraLargeDialog from "../UI/ExtraLargeDialog";
import {
  formatDateYYYYMMDD,
  formatTimestampHumanReadableTZ,
} from "utils/formatting";

interface ShippingManagerProps {
  customerId: number;
  customerName: string;
  dateStart: string;
  dateEnd: string;
  onClose: () => void;
  shippingDetails?: ShippingDetailRecord_T[];
  isLoading?: boolean;
}

const ShippingManager: React.FC<ShippingManagerProps> = ({
  customerId,
  customerName,
  dateStart,
  dateEnd,
  onClose,
  shippingDetails: propsShippingDetails,
  isLoading: propsIsLoading,
}) => {
  const theme = useTheme();
  const { showError } = useError();
  const {
    dialogType,
    openDialog,
    closeDialog: closeDialogBase,
  } = useDialog<ShippingDialogTypeT>();

  const {
    shippingDetails: localShippingDetails,
    isLoading: localIsLoading,
    isRefetching,
    detailsError: error,
    refetchAll,
  } = useShippingDataCoordinator(dateStart, dateEnd);
  // Use props if available, otherwise fall back to hook's data
  const allShippingDetails = propsShippingDetails ?? localShippingDetails;
  const isLoading = propsIsLoading ?? localIsLoading;

  // Filter data for this specific customer and add groupId
  const data = React.useMemo(() => {
    return (
      allShippingDetails
        ?.filter(
          (item: ShippingDetailRecord_T) => item.customer_id === customerId
        )
        ?.map((item: ShippingDetailRecord_T) => {
          // Extract date part from target_fulfillment_date (format: "2025-08-15 00:00:00")
          const datePart = item.target_fulfillment_date?.split(" ")[0] || "";
          // Create groupId by concatenating fulfillment_group_id and date
          const groupId = `${
            item.fulfillment_group_id || "unknown"
          }_${datePart}`;

          return {
            ...item,
            groupId,
          };
        }) || []
    );
  }, [allShippingDetails, customerId]);

  // mui row selection model
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);

  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(
    new Set()
  );

  const [fulfillmentGroups, setFulfillmentGroups] = React.useState<
    Array<{ id: number; name: string }>
  >([]);

  const uniqueGroups = React.useMemo(() => {
    if (!data) return [];

    const groupMap = new Map();

    for (const item of data) {
      if (item.groupId && !groupMap.has(item.groupId)) {
        groupMap.set(item.groupId, {
          id: item.groupId,
          name: item.fulfillment_group_name,
        });
      }
    }

    return Array.from(groupMap.values());
  }, [data]);

  React.useEffect(() => {
    setFulfillmentGroups(uniqueGroups);
  }, [uniqueGroups]);

  React.useEffect(() => {
    if (error) {
      showError(`Error fetching this week's shipping summary.`);
    }
  }, [error, showError]);

  const closeDialog = () => {
    closeDialogBase();
    refetchAll();
  };

  // calculate status
  const getRollupStatus = (items: ShippingDetailRecord_T[]): string => {
    if (!items?.length) return "--";

    // find the lowest status_display_order
    const lowestOrder = Math.min(
      ...items.map(
        (item) => item.status_display_order || Number.MAX_SAFE_INTEGER // could be 9999
      )
    );

    const itemWithLowestOrder = items.find(
      (item) => item.status_display_order === lowestOrder
    );

    // status or not started
    return itemWithLowestOrder?.fulfillment_status || "Not started";
  };

  // volume upgrade merge status
  const calculateVUMergeStatus = (items: ShippingDetailRecord_T[]): string => {
    // scheduled and bounce if none
    const scheduledVUs = items.filter((item) => item.volume_upgrade_scheduled);
    if (scheduledVUs.length === 0) return "--";

    // count with timestamp
    const completedVUs = scheduledVUs.filter(
      (item) => item.volume_upgrade_merged
    );

    // calc percentage
    const percentage = (completedVUs.length / scheduledVUs.length) * 100;

    // scheduled but none completed
    if (completedVUs.length === 0) return "Pending";

    // rounding but maybe percentage is better???
    return `${Math.round(percentage)}%`;
  };

  // simple calc for percentage of scheduled VUs
  const calculateVUScheduleStatus = (
    items: ShippingDetailRecord_T[]
  ): number | null => {
    if (!items?.length) return null;

    const totalItems = items.length;
    const scheduledVUs = items.filter(
      (item) => item.volume_upgrade_scheduled
    ).length;

    return scheduledVUs > 0 ? (scheduledVUs / totalItems) * 100 : null;
  };

  // todo: check lodash for convenience function
  const calculateVolumeSum = (
    items: ShippingDetailRecord_T[],
    key: keyof ShippingDetailRecord_T
  ): number => {
    return items.reduce((sum, item) => sum + ((item[key] as number) || 0), 0);
  };
  // group data by fulfillment_group_id for selection and expand/collapse
  const groupedData =
    data?.length > 0
      ? Object.values(
          data
            .filter(
              (item: any) =>
                item && item.customer_name === customerName && item.groupId
            )
            .reduce((acc: any, item: any) => {
              const groupId = item.groupId;

              // init group
              if (!acc[groupId]) {
                acc[groupId] = {
                  id: groupId,
                  fulfillment_group_id: item.fulfillment_group_id,
                  fulfillment_group_name: item.fulfillment_group_name,
                  placesets: item.placesets,
                  items: [],
                };
              }

              // add item
              if (item && item.id) {
                acc[groupId].items.push({
                  ...item,
                  id: `${groupId}-${item.id}`,
                });
              }

              // find highest priority status
              acc[groupId].status = getRollupStatus(acc[groupId].items);

              // calc vu merge status
              acc[groupId].vu_merged = calculateVUMergeStatus(
                acc[groupId].items
              );

              // calc vu schedule status
              acc[groupId].vu_scheduled = calculateVUScheduleStatus(
                acc[groupId].items
              );

              // calc current volume
              acc[groupId].current_volume = calculateVolumeSum(
                acc[groupId].items,
                "volume"
              );

              // calc previous volume
              acc[groupId].previous_volume = calculateVolumeSum(
                acc[groupId].items,
                "previous_volume"
              );

              return acc;
            }, {})
        )
      : [];

  // todo: look into moving the grouping logic to the backend
  // or at least a helper function. also used in scheduling UI
  const flattenedData = React.useMemo(() => {
    const result: any[] = [];

    groupedData.forEach((group: any) => {
      // parent
      result.push({
        ...group,
        id: `fulfillmentparent-${group.id}`,
        isParent: true,
        level: 0,
      });

      // children
      group.items.forEach((item: any) => {
        result.push({
          ...item,
          id: `fulfillmentchild-${item.id}`,
          isParent: false,
          level: 1,
          parentId: `fulfillmentparent-${group.id}`,
        });
      });
    });

    return result;
  }, [groupedData]);

  const columns: GridColDef[] = [
    {
      field: "fulfillment_group_id",
      headerName: "FG ID",
      width: 90,
      renderCell: (params: any) => {
        const level = params.row.level;
        const isParent = params.row.isParent;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pl: level * 4,
            }}
          >
            {isParent ? (
              <IconButton
                size="small"
                onClick={() => handleExpandClick(params.row.id)}
              >
                {expandedRows.has(params.row.id) ? (
                  <RiArrowDownSLine />
                ) : (
                  <RiArrowRightSLine />
                )}
              </IconButton>
            ) : null}
            <Box sx={{ fontWeight: isParent ? 600 : 400 }}>
              {isParent ? (
                <span> {params.row.fulfillment_group_id}</span>
              ) : null}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "delivery_id",
      headerName: "Delivery ID",
      width: 100,
      valueGetter: (value, row) => {
        return row.isParent ? "--" : row.delivery_id;
      },
    },
    {
      field: "fulfillment_group_name",
      headerName: "Name",
      width: 210,
      valueGetter: (value, row) => {
        return row.isParent ? row.fulfillment_group_name : row.delivery_name;
      },
      renderCell: (params: any) => {
        // conditional render based on isParent
        // this is a bit of a hack to keep names aligned
        // todo: might need nested tables, check sorting
        // might need value getter
        return (
          <Box
            sx={{
              fontWeight: params.row.isParent ? 600 : 400,
            }}
          >
            {params.row.isParent
              ? params.row.fulfillment_group_name
              : params.row.delivery_name}
          </Box>
        );
      },
    },
    {
      field: "placesets",
      headerName: "Placesets",
      width: 90,
      type: "number",
      valueGetter: (value, row) => {
        return row.isParent ? row.items.length : "--";
      },
    },
    {
      field: "target_fulfillment_date",
      headerName: "Target Date",
      width: 120,
      renderCell: (params: any) => {
        // ad hoc design: put date in parent row if all times are the same
        // todo: check if this is the best way to do this
        // todo: double-check date formatting and calcuations
        if (params.row.isParent) {
          const uniqueDates = new Set(
            params.row.items
              .map(
                (item: ShippingDetailRecord_T) => item.target_fulfillment_date
              )
              .filter(Boolean)
          );

          if (uniqueDates.size === 1) {
            const dateStr = Array.from(uniqueDates)[0] as string;
            const date = new Date(dateStr);
            const dayOfWeek = formatInTimeZone(date, "UTC", "EEE");
            return (
              <Box
                sx={{
                  color: dayOfWeek === "Thu" ? "inherit" : "#E74C3C",
                }}
              >
                {formatInTimeZone(date, "UTC", "EEE, yyyy-MM-dd")}
              </Box>
            );
          }
          return "--";
        }

        // child row
        if (!params.row.target_fulfillment_date) return "--";

        const date = new Date(params.row.target_fulfillment_date);
        const dayOfWeek = formatInTimeZone(date, "UTC", "EEE");
        return (
          <Box
            sx={{
              color: dayOfWeek === "Thu" ? "inherit" : "#E74C3C",
            }}
          >
            {formatInTimeZone(date, "UTC", "EEE, yyyy-MM-dd")}
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 200,
      valueGetter: (value, row) => {
        return row.isParent ? row.status : row.fulfillment_status;
      },
      renderCell: (params: any) => {
        const status = params.row.isParent
          ? params.row.status // rollup status
          : params.row.fulfillment_status;
        return <ShippingStatusSummary status={status || "Not started"} />;
      },
    },
    {
      field: "fgs_id",
      headerName: "FGS ID",
      width: 120,
      renderCell: (params: any) => {
        const { isParent, items } = params.row;

        const fgsId =
          isParent && items && items.length > 0
            ? items[0].fgs_id
            : params.row.fgs_id;

        return fgsId ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              height: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>{fgsId}</Box>
            {isParent && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Link
                  to={`https://kuebiko.dataplor.com/customers/${params.row.customer_id}/fulfillment-groups/${params.row.fulfillment_group_id}/fulfillment-group-shipments/${fgsId}/data`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <RiExternalLinkLine size={16} />
                </Link>
              </Box>
            )}
          </Box>
        ) : (
          "--"
        );
      },
    },
    {
      field: "delivery_fulfillment_id",
      headerName: "DF ID",
      width: 120,
      renderCell: (params: any) => {
        const { isParent } = params.row;
        return isParent ? "--" : params.row.delivery_fulfillment_id;
      },
    },
    {
      field: "current_volume",
      headerName: "Current Volume",
      width: 140,
      type: "number",
      valueGetter: (value, row) => {
        return row.isParent ? row.current_volume : row.volume;
      },
    },
    {
      field: "previous_volume",
      headerName: "Previous Volume",
      width: 140,
      type: "number",
      valueGetter: (value, row) => {
        return row.isParent ? row.previous_volume : row.previous_volume;
      },
    },
    {
      field: "vu_scheduled",
      headerName: "VU Scheduled",
      width: 120,
      type: "boolean",
      valueGetter: (value, row) => {
        return row.isParent
          ? row.vu_scheduled > 0
          : row.volume_upgrade_scheduled === true;
      },
      renderCell: (params: any) => {
        // parent true if any via uv_scheduled
        // child true if volume_upgrade_scheduled
        const icon = params.row.isParent ? (
          params.row.vu_scheduled ? (
            <RiCheckLine color={theme.palette.success.main} size={20} />
          ) : (
            <RiCloseFill color={theme.palette.error.main} size={20} />
          )
        ) : params.row.volume_upgrade_scheduled ? (
          <RiCheckLine color={theme.palette.success.main} size={20} />
        ) : (
          <RiCloseFill color={theme.palette.error.main} size={20} />
        );

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        );
      },
    },
    {
      field: "vu_merged",
      headerName: "VU Merged",
      width: 260,
      valueGetter: (value, row) => {
        if (!row.isParent) {
          if (!row.volume_upgrade_scheduled) return "--";
          return row.volume_upgrade_merged || "Pending";
        }

        return row.vu_merged;
      },
      renderCell: (params: any) => {
        if (params.row.isParent) {
          return params.row.vu_merged;
        }
        return params.row.volume_upgrade_scheduled
          ? formatTimestampHumanReadableTZ(params.row.volume_upgrade_merged)
          : "--";
      },
    },
    {
      field: "export_id",
      headerName: "Export ID",
      width: 150,
      renderCell: (params: any) => {
        // borrowed from ShippingStatusDetailsTable
        // updated link to kuebiko-web
        return params.row.isParent ? (
          "--"
        ) : params.row.export_id ? (
          <Link
            to={`https://kuebiko.dataplor.com/deliveries/${params.row.delivery_id}/exports/${params.row.export_id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {params.value} <RiExternalLinkLine size="14" />
          </Link>
        ) : (
          <Link
            to={`https://kuebiko.dataplor.com/deliveries/${params.row.delivery_id}/exports`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Exports <RiExternalLinkLine size="14" />
          </Link>
        );
      },
    },
    {
      field: "destination",
      headerName: "Destination",
      width: 150,
      valueGetter: (value, row) => {
        if (!row.isParent) {
          return row.fulfillment_destination;
        }

        // use set for uniques
        const uniqueDestinations = new Set(
          row.items.map(
            (item: ShippingDetailRecord_T) => item.fulfillment_destination
          )
        );

        return uniqueDestinations.size === 1
          ? Array.from(uniqueDestinations)[0]
          : "--";
      },
    },
    {
      field: "most_recent_delivery",
      headerName: "Most Recent Delivery",
      width: 180,
      valueGetter: (value, row) => {
        if (row.isParent) return "--";
        return row.previous_delivery_date ? row.previous_delivery_date : "N/A";
      },
      renderCell: (params: any) => {
        return params.row.previous_delivery_date
          ? formatDateYYYYMMDD(params.row.previous_delivery_date)
          : "--";
      },
    },
    {
      field: "next_delivery",
      headerName: "Next Delivery",
      width: 180,
      valueGetter: (value, row) => {
        if (row.isParent) return "--";
        return row.next_delivery_date ? row.next_delivery_date : "N/A";
      },
      renderCell: (params: any) => {
        return params.row.next_delivery_date
          ? formatDateYYYYMMDD(params.row.next_delivery_date)
          : "--";
      },
    },
    {
      field: "license_end_date",
      headerName: "License End Date",
      width: 180,
      valueGetter: (value, row) => {
        if (row.isParent) return "--";
        return row.license_end_date ? row.license_end_date : "N/A";
      },
      renderCell: (params: any) => {
        return params.row.license_end_date
          ? formatDateYYYYMMDD(params.row.license_end_date)
          : "--";
      },
    },
  ];

  const handleExpandClick = (groupId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(groupId)) {
      newExpandedRows.delete(groupId);
    } else {
      newExpandedRows.add(groupId);
    }
    setExpandedRows(newExpandedRows);
  };

  // expand/collapse logic to hide/show children
  // todo: disable if no children or default to
  // to expanded if only one parent in intial dataset
  const processedData = React.useMemo(() => {
    return flattenedData.filter((row: any) => {
      if (row.isParent) return true;
      return expandedRows.has(row.parentId);
    });
  }, [flattenedData, expandedRows]);

  // selection changes, might be a more elegant way to do this
  const handleRowSelectionModelChange = React.useCallback(
    (newRowSelectionModel: GridRowSelectionModel) => {
      const updatedSelection = new Set(newRowSelectionModel);

      const previousSelection = new Set(rowSelectionModel);

      const added = [...updatedSelection].filter(
        (x) => !previousSelection.has(x)
      );

      const removed = [...previousSelection].filter(
        (x) => !updatedSelection.has(x)
      );

      const selections = new Set(updatedSelection);

      added.forEach((id) => {
        const row = flattenedData.find((r) => r.id === id);

        if (row?.isParent) {
          flattenedData
            .filter((r) => !r.isParent && r.parentId === row.id)
            .forEach((child) => selections.add(child.id));
        }
      });

      // unselect all children
      removed.forEach((id) => {
        const row = flattenedData.find((r) => r.id === id);

        if (row?.isParent) {
          flattenedData
            .filter((r) => !r.isParent && r.parentId === row.id)
            .forEach((child) => selections.delete(child.id));
        }
      });

      // if child was selected/deselected, check if we need to handle parent
      flattenedData
        .filter((r) => r.isParent)
        .forEach((parent) => {
          // get children
          const children = flattenedData.filter(
            (r) => !r.isParent && r.parentId === parent.id
          );

          // get selected children
          const selectedChildren = children.filter((child) =>
            selections.has(child.id)
          );

          // all children are selected, add/select parent
          if (selectedChildren.length === children.length) {
            selections.add(parent.id);
          } else {
            // not all children are selected, remove/unselect parent
            // todo: figure out indeterminate state
            selections.delete(parent.id);
          }
        });

      setRowSelectionModel(Array.from(selections));
    },
    [flattenedData, rowSelectionModel]
  );

  const getSelectedData = (): SelectedShippingData_T => {
    const fullySelectedGroups: SelectedShippingGroup_T[] = [];
    const partiallySelectedGroups: SelectedShippingGroup_T[] = [];

    flattenedData
      .filter((row) => row.isParent)
      .forEach((parent) => {
        const children = flattenedData.filter(
          (r) => !r.isParent && r.parentId === parent.id
        );

        const selectedChildren = children.filter((child) =>
          rowSelectionModel.includes(child.id)
        );

        if (selectedChildren.length === children.length) {
          fullySelectedGroups.push({
            fulfillmentGroupId: parent.fulfillment_group_id,
            name: parent.fulfillment_group_name,
            shipments: children,
          });
        } else if (selectedChildren.length > 0) {
          partiallySelectedGroups.push({
            fulfillmentGroupId: parent.fulfillment_group_id,
            name: parent.fulfillment_group_name,
            shipments: selectedChildren,
          });
        }
      });

    return {
      fullySelectedGroups,
      partiallySelectedGroups,
      totalSelectedShipments: rowSelectionModel.filter((id) =>
        id.toString().startsWith("fulfillmentchild-")
      ).length,
    };
  };

  const renderDialogContent = () => {
    const dialogProps = {
      onClose: closeDialog,
      customerId,
      customerName,
      dateStart,
      dateEnd,
      selectedData: getSelectedData(),
      fulfillmentGroups,
    };

    switch (dialogType) {
      case ShippingDialogTypesT.REFRESH_EXPORT:
        return <RefreshExportDialog {...dialogProps} />;
      case ShippingDialogTypesT.EXPORT:
        return <ExportDialog {...dialogProps} />;
      case ShippingDialogTypesT.CREATE_SHIPMENT:
        return (
          <CreateShipmentDialog
            {...dialogProps}
            fulfillmentGroups={fulfillmentGroups}
          />
        );
      case ShippingDialogTypesT.DELIVER_SHIPMENT:
        return <DeliverShipmentDialog {...dialogProps} />;
      case ShippingDialogTypesT.MARK_DELIVERED:
        return <MarkDeliveredDialog {...dialogProps} />;
      case ShippingDialogTypesT.DELIVER_CHECKSUM:
        return <ChecksumDialog {...dialogProps} />;
      default:
        return null;
    }
  };

  return (
    <ExtraLargeDialog
      open={true}
      onClose={onClose}
      screenPercentage={90}
      title="Shipping Manager"
    >
      <DialogTitle>
        Shipping Manager: {customerName} - {customerId}
        <IconButton
          aria-label="close"
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <RiCloseLine />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          justifyContent="space-between"
          gap={1}
          mt={2}
          mb={2}
        >
          <Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => refetchAll()}
              disabled={isLoading || isRefetching}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <RiResetRightLine
                  size={16}
                  style={{
                    animation:
                      isLoading || isRefetching
                        ? "spin 1s linear infinite"
                        : "none",
                  }}
                />
                {isLoading ? "Loading" : isRefetching ? "Reloading" : "Reload"}
              </Box>
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openDialog(ShippingDialogTypesT.EXPORT)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <RiExportLine size={16} />
                Export
              </Box>
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openDialog(ShippingDialogTypesT.CREATE_SHIPMENT)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <RiAddCircleLine size={16} />
                Create Shipment
              </Box>
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openDialog(ShippingDialogTypesT.REFRESH_EXPORT)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <RiRefreshLine size={16} />
                Refresh Shipment
              </Box>
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openDialog(ShippingDialogTypesT.DELIVER_SHIPMENT)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <RiSendPlaneLine size={16} />
                Deliver Shipment
              </Box>
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openDialog(ShippingDialogTypesT.MARK_DELIVERED)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <RiCheckDoubleLine size={16} />
                Mark as Delivered
              </Box>
            </Button>
            {customerId === 1 && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() =>
                  openDialog(ShippingDialogTypesT.DELIVER_CHECKSUM)
                }
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <RiFileTextLine size={16} />
                  Deliver Checksum
                </Box>
              </Button>
            )}
          </Box>
        </Box>
        <Box>
          <GaneshDataGrid
            gridId="shipping-export-manager-grid"
            defaultGridState={{
              density: "compact",
            }}
            columns={columns}
            customHeight="650px"
            data={processedData}
            headerFilters={false}
            loading={isLoading}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            keepNonExistentRowsSelected
            sx={{
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "transparent !important",
              },
              "& .MuiDataGrid-cell": {
                outline: "none !important",
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
      {renderDialogContent()}
    </ExtraLargeDialog>
  );
};

export default ShippingManager;
